/** @format */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'src/store';
import { getShopOrders } from 'src/slices/shopOrders';
import { SHOP_ORDERS_HISTORY_URL } from 'src/constants';
import ShopOrdersSkeleton from './shopOrdersSkeleton';

const formatOrderDate = date => {
  if (!date) return '';
  if (/^\d{2}\.\d{2}\.\d{4}/.test(date)) return date;
  if (/^\d{2}\.\d{2}\.\d{2}$/.test(date)) {
    const [day, month, year] = date.split('.');
    return `${day}.${month}.20${year}`;
  }
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = String(parsed.getFullYear());
  return `${day}.${month}.${year}`;
};

const getStatusClass = status => {
  const normalized = String(status || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '');
  if (normalized === 'inprogress' || normalized === 'processing') return 'inprogress';
  if (normalized === 'onhold') return 'onhold';
  return 'submitted';
};

const getTrackingUrl = trackingId => {
  if (!Array.isArray(trackingId) || trackingId.length === 0) return null;
  const first = trackingId[0];
  return typeof first === 'string' ? first : (first?.url ?? first?.link ?? null);
};

const normalizeOrder = order => ({
  id: order.order_id,
  number: order.order_id,
  date: order.order_date,
  price: order.order_total,
  status: order.status,
  trackingUrl: getTrackingUrl(order.tracking_id),
  orderUrl: order.my_account_order_link
});

const getTrackingHref = order => order.trackingUrl || SHOP_ORDERS_HISTORY_URL;

export default function ShopOrdersWidget({ data }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orders, ordersLoading } = useSelector(state => state.shopOrders);
  const title = data?.title?.trim() || t('widget.shopOrdersTitle');

  useEffect(() => {
    if (orders === null) {
      dispatch(getShopOrders());
    }
  }, [orders, dispatch]);

  const normalizedOrders = (orders || []).map(normalizeOrder);

  return (
    <div className="shop-orders" data-width={data.w} data-size={data.size} data-height={data.h}>
      <div className="shop-orders__title">
        <h2>{title}</h2>
      </div>

      {ordersLoading && orders === null ? (
        <ShopOrdersSkeleton />
      ) : (
        <div className="shop-orders__content">
          {normalizedOrders.length > 0 ? (
            <>
              <div className="shop-orders__table">
                <div className="shop-orders__row shop-orders__row--header">
                  <div className="shop-orders__col shop-orders__col--number">{t('widget.shopNumber')}</div>
                  <div className="shop-orders__col shop-orders__col--date">{t('widget.shopDate')}</div>
                  <div className="shop-orders__col shop-orders__col--price">{t('widget.shopPrice')}</div>
                  <div className="shop-orders__col shop-orders__col--status">{t('common.status')}</div>
                  <div className="shop-orders__col shop-orders__col--action">{t('widget.shopAction')}</div>
                </div>
                <div className="shop-orders__body">
                  {normalizedOrders.map((order, i) => (
                    <div className="shop-orders__row" key={order.id + i}>
                      <div className="shop-orders__col shop-orders__col--number">
                        <a href={order.orderUrl || '#'}>{order.number}</a>
                        <div className="shop-orders__tracking-stacked">
                          <a href={getTrackingHref(order)} target="_blank" rel="noopener noreferrer">
                            Tracking
                          </a>
                        </div>
                      </div>
                      <div className="shop-orders__col shop-orders__col--date">{formatOrderDate(order.date)}</div>
                      <div className="shop-orders__col shop-orders__col--price">{order.price ?? ''}</div>
                      <div className="shop-orders__col shop-orders__col--status">
                        <span className={`shop-orders__status shop-orders__status--${getStatusClass(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="shop-orders__col shop-orders__col--action">
                        <a href={getTrackingHref(order)} target="_blank" rel="noopener noreferrer">
                          Tracking
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="shop-orders__empty">{t('widget.noOrders')}</p>
          )}

          <div className="shop-orders__link">
            <a href={SHOP_ORDERS_HISTORY_URL} target="_blank" rel="noopener noreferrer">
              {t('widget.allOrders')}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
